import type { RpaSegmentedTask } from '../../contracts/projects.types';
import { WORKFLOW_CONTEXT_USER_REQUEST_MARKER } from '../../contracts/workflows/lib/workflow-context-marker';
import type { ProjectsComposerPromptDraft } from './projects-composer-editor-draft';
import { projectsComposerDraftDisplayText } from './projects-composer-editor-draft';

export interface BuildProjectsComposerMessageArgs {
  readonly linkedContextPreamble?: string;
  readonly text: string;
}

export const buildProjectsComposerMessage = ({
  linkedContextPreamble = '',
  text,
}: BuildProjectsComposerMessageArgs): string =>
  joinPromptParts([linkedContextPreamble, text]);

export const isProjectsComposerSubmittable = (text: string, hasSkill: boolean): boolean =>
  text.trim().length > 0 || hasSkill;

export const hasRpaSegmentedTextSegments = (task: RpaSegmentedTask): boolean =>
  task.segments.some((segment) => segment.kind === 'text');

export interface ProjectsComposerSubmitPromptsArgs {
  readonly draft: ProjectsComposerPromptDraft;
  readonly linkedContextPreamble?: string;
  readonly computerUseSkill?: RpaSegmentedTask;
}

export interface ProjectsComposerSubmitPrompts {
  /** Wire prompt for `POST /projects/{id}/turns` (`prompt` field). */
  readonly wireText: string;
  /** User-visible slash-token prompt for the optimistic chat bubble. */
  readonly displayText: string;
}

/**
 * Split the composer turn into wire vs display prompts.
 *
 * When a delegated ``computer_use_skill`` carries free-text segments, the
 * backend appends those segments to the turn prompt server-side. Sending
 * the same text in both places would duplicate the durable transcript.
 */
export const projectsComposerSubmitPrompts = ({
  draft,
  linkedContextPreamble = '',
  computerUseSkill,
}: ProjectsComposerSubmitPromptsArgs): ProjectsComposerSubmitPrompts => {
  const displayBody = projectsComposerDraftDisplayText(draft) ?? '';
  const displayText = buildProjectsComposerMessage({
    linkedContextPreamble,
    text: displayBody,
  });
  const wireBody =
    computerUseSkill && hasRpaSegmentedTextSegments(computerUseSkill) ? '' : displayBody;
  const wireText = buildProjectsComposerMessage({
    linkedContextPreamble,
    text: wireBody,
  });

  return { wireText, displayText };
};

export interface BuildWorkflowTurnWireTextArgs {
  readonly instructions: string;
  /** Wire text the user typed alongside the workflow chip. */
  readonly additionalText?: string;
  readonly linkedContextPreamble?: string;
}

/**
 * Build the wire prompt for a mid-chat workflow invocation by combining
 * the workflow's full instructions with any user-supplied additional text.
 * The additional text follows `WORKFLOW_CONTEXT_USER_REQUEST_MARKER` so the
 * transcript bubble can show the clinician's words instead of the brief.
 */
export const buildWorkflowTurnWireText = ({
  instructions,
  additionalText = '',
  linkedContextPreamble = '',
}: BuildWorkflowTurnWireTextArgs): string => {
  const trimmed = additionalText.trim();
  const text = trimmed
    ? `${instructions}\n\n${WORKFLOW_CONTEXT_USER_REQUEST_MARKER}\n\n${trimmed}`
    : instructions;
  return buildProjectsComposerMessage({ linkedContextPreamble, text });
};

const joinPromptParts = (parts: readonly string[]): string =>
  parts
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join('\n\n');

const ROUTINE_TURN_USER_REQUEST_NEEDLE = `\n${WORKFLOW_CONTEXT_USER_REQUEST_MARKER}\n`;

/**
 * Split a mid-chat routine turn (`buildWorkflowTurnWireText`) into the routine
 * brief and the clinician's words after the marker. The caller only trusts the
 * split when `brief` is a known routine's instructions, so a message cannot
 * hide its own text behind a copied marker.
 */
export const splitRoutineTurnBody = (
  body: string
): { brief: string; request: string } | null => {
  const markerAt = body.lastIndexOf(ROUTINE_TURN_USER_REQUEST_NEEDLE);
  if (markerAt < 0) return null;
  const brief = body.slice(0, markerAt).trim();
  const request = body.slice(markerAt + ROUTINE_TURN_USER_REQUEST_NEEDLE.length).trim();
  if (!brief || !request) return null;
  return { brief, request };
};
